import { AdminClient } from 'graphql/clients'
import { ButtonGroup, ToggleButtonGroupProps, Typography, useHeader, useTheme } from '@forgeglobal/design-framework'
import {
  CrmMetadataDocument,
  CrmMetadataQuery,
  DoNotContactReason,
  useAppDataQuery,
} from 'graphql/bindings/AdminBindings'
import { FilterChangedEvent, GridApi, GridOptions, ValueFormatterParams } from 'ag-grid-community'
import {
  FullscreenTable,
  defaultNumbDateOptions,
  defaultTableBooleanColumn,
  defaultTextOptions,
  gridOptions,
  noInteractionColumnHeader,
} from 'isolated-design-library'
import { formatAbbreviatedNumber } from 'util/NumberUtils'
import { formatEnumMulti, formatNetWorthRange } from 'util/EnumUtils'
import { getNames } from 'country-list'
import { monthDayYear } from 'util/DateUtils'
import { regions, targetCheckSize } from 'util/Enums'
import AccountsDatasource, { doNotContactOptions } from 'util/AccountsDataSource'
import React, { useState } from 'react'
import capitalize from 'lodash/capitalize'
import concat from 'lodash/concat'
import values from 'lodash/values'

const unwrapFormatterParams = (f: (params: any) => string) => {
  return (params: ValueFormatterParams) => {
    if (params?.value === null || typeof params === undefined) return ''
    return f(params.value)
  }
}

const formatCapitalizeParams = unwrapFormatterParams(capitalize)
const formatDateParams = unwrapFormatterParams(monthDayYear)
const formatMoneyRangeParams = unwrapFormatterParams(formatNetWorthRange)
const formatAbbrMoneyParams = unwrapFormatterParams((value: number) => `$${formatAbbreviatedNumber(value)}`)

const formatEnumMultiParmas = (params: ValueFormatterParams) => {
  if (params?.value === null || typeof params === undefined) return ''
  return formatEnumMulti(params.value)
}

export const Accounts: React.FC = () => {
  useHeader()
  const [gridApi, setGridApi] = useState<GridApi | null>()
  const [dncFilters, setDNCFilters] = useState<string[] | null>([])
  const headerQuery = AdminClient.query<CrmMetadataQuery>({
    query: CrmMetadataDocument,
  }).then(({ data }) => {
    const clientTypeNames = (data?.client_types ?? []).filter(s => s.account_type !== 'issuer').map(s => s.name)
    const sectorNames = (data?.sectors ?? []).map(s => s.name).sort()
    const issuerNames = Array.from(
      new Set((data?.issuers ?? []).map(issuer => issuer.issuer_account?.company?.name ?? '').filter(name => !!name))
    )
    const adminNames = Array.from(
      new Set(
        (data?.adminsWithAccountCoverage ?? [])
          .map(admin => `${admin.first_name} ${admin.last_name}`)
          .filter(name => !!name)
      )
    )

    return { clientTypeNames, sectorNames, issuerNames, adminNames }
  })

  type SetFilterOpts = Array<string | null>
  const clientTypeNames = (params: any) =>
    headerQuery.then(({ clientTypeNames }) => params.success([null, ...clientTypeNames]))
  const sectorNames = (params: any) =>
    headerQuery.then(({ sectorNames }) => params.success(([null] as SetFilterOpts).concat(sectorNames)))
  const issuerNames = (params: any) =>
    headerQuery.then(({ issuerNames }) => params.success(([null] as SetFilterOpts).concat(issuerNames)))
  const adminNames = (params: any) => headerQuery.then(({ adminNames }) => params.success(adminNames))

  const columnDefs: GridOptions['columnDefs'] = [
    { field: 'name', filter: 'agTextColumnFilter', pinned: 'left', ...defaultTextOptions },
    {
      field: 'do_not_contact_reasons',
      headerName: 'Deal contact',
      filterParams: {
        values: concat(['OK to contact'], values(doNotContactOptions)),
        suppressSorting: true,
        newRowsAction: 'keep',
      },
    },
    {
      field: 'investor_level',
      headerName: 'Investor Level',
      filter: 'agSetColumnFilter',
      filterParams: { values: [null, 0, 1, 2, 3, 4], newRowsAction: 'keep' },
    },
    {
      field: 'business_development_tier',
      headerName: 'BD Tier',
      filter: 'agSetColumnFilter',
      filterParams: { values: [null, 1, 2, 3, 4], newRowsAction: 'keep' },
    },
    {
      field: 'type',
      filter: 'agSetColumnFilter',
      filterParams: { values: ['individual', 'organization'], newRowsAction: 'keep' },
      valueFormatter: formatCapitalizeParams,
    },
    {
      field: 'investor_shareholder',
      headerName: 'Investor/Shareholder',
      filter: 'agSetColumnFilter',
      filterParams: { values: ['Investor', 'Shareholder'], newRowsAction: 'keep' },
    },
    {
      field: 'owners',
      headerName: 'Coverage',
      filter: 'agSetColumnFilter',
      filterParams: { values: adminNames, newRowsAction: 'keep' },
    },
    {
      field: 'client_type',
      headerName: 'Client Type',
      filter: 'agSetColumnFilter',
      filterParams: { values: clientTypeNames, newRowsAction: 'keep' },
    },
    { field: 'email', filter: 'agTextColumnFilter', ...defaultTextOptions },
    { field: 'phone', filter: 'agTextColumnFilter', ...defaultTextOptions },
    {
      field: 'points_of_contact',
      headerName: 'Points of Contact',
      filter: false,
    },
    {
      field: 'intermediary_name',
      headerName: 'Intermediary name',
      filter: 'agTextColumnFilter',
      ...defaultTextOptions,
    },
    {
      field: 'intermediary_email',
      headerName: 'Intermediary email',
      filter: 'agTextColumnFilter',
      ...defaultTextOptions,
    },
    {
      field: 'intermediary_title',
      headerName: 'Intermediary title',
      filter: 'agTextColumnFilter',
      ...defaultTextOptions,
    },
    {
      field: 'companies_of_interest',
      headerName: 'Companies of Interest',
      filter: 'agSetColumnFilter',
      filterParams: { values: issuerNames, newRowsAction: 'keep' },
    },
    {
      field: 'purchased_companies',
      filter: 'set',
      headerName: 'Purchased Companies',
      filterParams: { values: issuerNames, newRowsAction: 'keep' },
    },
    {
      field: 'holdings',
      filter: 'agSetColumnFilter',
      filterParams: { values: issuerNames, newRowsAction: 'keep' },
    },
    {
      field: 'average_order_size',
      filter: 'agNumberColumnFilter',
      headerName: 'Average Order Size',
      valueFormatter: formatAbbrMoneyParams,
      ...defaultNumbDateOptions,
    },
    {
      field: 'order_structures',
      headerName: 'Order Structures',
      filter: 'set',
      filterParams: {
        values: ['direct_share_transfer', 'forward_backed_fund_interest', 'share_backed_fund_interest'],
        newRowsAction: 'keep',
      },
      valueFormatter: formatEnumMultiParmas,
    },
    {
      field: 'num_active_deals',
      filter: 'agNumberColumnFilter',
      headerName: 'Active Deals',
      ...defaultNumbDateOptions,
    },
    {
      field: 'num_closed_deals',
      filter: 'agNumberColumnFilter',
      headerName: 'Closed Deals',
      ...defaultNumbDateOptions,
    },
    {
      field: 'num_introductions',
      filter: 'agNumberColumnFilter',
      headerName: 'Introductions',
      ...defaultNumbDateOptions,
    },
    {
      field: 'last_order_created_date',
      filter: 'agDateColumnFilter',
      ...defaultNumbDateOptions,
      headerName: 'Last Order Created Date',
      valueFormatter: formatDateParams,
    },
    {
      field: 'last_order_closed_date',
      filter: 'agDateColumnFilter',
      headerName: 'Last Order Date',
      valueFormatter: formatDateParams,
      ...defaultNumbDateOptions,
    },
    {
      field: 'has_transacted',
      headerName: 'Has Transacted',
      ...defaultTableBooleanColumn,
    },
    {
      field: 'has_done_primaries',
      headerName: 'Has Done Primaries',
      ...defaultTableBooleanColumn,
    },
    {
      field: 'has_done_secondaries',
      headerName: 'Has Done Secondaries',
      ...defaultTableBooleanColumn,
    },
    {
      field: 'net_worth',
      filter: 'agSetColumnFilter',
      headerName: 'Net Worth',
      filterParams: {
        values: ['<1m', '1m-2m', '2m-5m', '5m-10m', '10m-25m', '25m-100m', '100m+'],
        newRowsAction: 'keep',
      },
      valueFormatter: formatMoneyRangeParams,
    },
    {
      field: 'aum',
      filter: 'agSetColumnFilter',
      filterParams: {
        values: ['<25m', '25m-50m', '50m-250m', '250m-500m', '500m-1b', '1b-10b', '10b+'],
        newRowsAction: 'keep',
      },
    },
    {
      field: 'region',
      filter: 'agSetColumnFilter',
      headerName: 'Region',
      filterParams: {
        values: [
          null,
          'Asia',
          'Central America and Caribbean',
          'Central Asia',
          'Europe',
          'East Asia',
          'Middle East and North Africa',
          'North America',
          'Oceania',
          'South America',
          'South Asia',
          'Sub-Saharan Africa',
        ],
        newRowsAction: 'keep',
      },
    },
    {
      field: 'country',
      filter: 'agSetColumnFilter',
      filterParams: {
        values: ([null] as Array<string | null>).concat(getNames()),
        newRowsAction: 'keep',
      },
    },
    { field: 'state', filter: 'agTextColumnFilter', ...defaultTextOptions },
    { field: 'city', filter: 'agTextColumnFilter', ...defaultTextOptions },
    {
      field: 'aum_secondaries',
      filter: 'agSetColumnFilter',
      headerName: 'Aum Secondaries',
      filterParams: { values: ['0-10%', '10-25%', '25-50%', '50-75%', '75-100%'], newRowsAction: 'keep' },
      valueFormatter: formatMoneyRangeParams,
    },
    {
      field: 'investment_structure_comfort',
      filter: 'agSetColumnFilter',
      headerName: 'Investment Structure Comfort',
      filterParams: {
        values: ['direct_share_transfer', 'forward_backed_fund_interest', 'share_backed_fund_interest'],
        newRowsAction: 'keep',
      },
    },
    {
      field: 'minimum_company_valuation',
      filter: 'agNumberColumnFilter',
      headerName: 'Min Valuation',
      ...defaultNumbDateOptions,
    },
    {
      field: 'minimum_company_revenue',
      filter: 'agNumberColumnFilter',
      headerName: 'Min Revenue',
      ...defaultNumbDateOptions,
    },
    {
      field: 'minimum_growth_rate',
      filter: 'agNumberColumnFilter',
      headerName: 'Min Growth Rate',
      ...defaultNumbDateOptions,
    },
    {
      field: 'only_profitable_companies',
      headerName: 'Only Profitable',
      ...defaultTableBooleanColumn,
    },
    {
      field: 'opportunities_of_interest',
      filter: 'agSetColumnFilter',
      headerName: 'Opportunities of Interest',
      filterParams: {
        values: [null, 'Primaries', 'Secondaries', 'Limited Partnership Interests', 'Synthetics'],
        newRowsAction: 'keep',
      },
    },
    {
      field: 'target_regions',
      filter: 'agSetColumnFilter',
      headerName: 'Target Regions',
      filterParams: {
        values: [null, ...regions],
        newRowsAction: 'keep',
      },
    },
    {
      field: 'target_check_size',
      filter: 'agSetColumnFilter',
      headerName: 'Target Check Size',
      filterParams: {
        values: targetCheckSize.map((obj: { label: string; value: string }) => obj.value),
        newRowsAction: 'keep',
      },
      valueFormatter: formatMoneyRangeParams,
    },
    {
      field: 'has_cash_on_hand',
      headerName: 'Has Cash on Hand',
      ...defaultTableBooleanColumn,
    },
    {
      field: 'sectors_of_interest',
      filter: 'agSetColumnFilter',
      filterParams: { values: sectorNames, newRowsAction: 'keep' },
      headerName: 'Sectors of Interest',
    },
    {
      field: 'url',
      headerName: 'Detail',
      pinned: 'right',
      ...noInteractionColumnHeader,
    },
  ]

  const ds = new AccountsDatasource(gridOptions)
  const { data: appDataQuery } = useAppDataQuery()
  const persistStateForDimensions = {
    tableName: 'accounts',
    userId: appDataQuery ? appDataQuery.currentAdmin.id : 'anonymous',
  }

  const { spacing } = useTheme()

  const onGridReady = (gridApi: GridApi) => {
    setGridApi(gridApi)
    setTimeout(() => {
      let doNotContactFilter = gridApi.getFilterInstance('do_not_contact_reasons')
      doNotContactFilter.setModel(['OK to contact'])
      gridApi.onFilterChanged()
    })
  }

  const onFilterChanged = (event: FilterChangedEvent) => {
    let gridApi = event.api
    let filter = gridApi.getFilterInstance('do_not_contact_reasons')
    let model = filter.getModel()
    if (model) {
      setDNCFilters(model.values)
    } else {
      setDNCFilters(null)
    }
  }

  return (
    <FullscreenTable
      serverSideDatasource={ds}
      columnDefs={columnDefs}
      handleGridReady={onGridReady}
      handleGridReset={onGridReady}
      handleFilterChanged={onFilterChanged}
      persistStateForDimensions={persistStateForDimensions}>
      <div style={{ display: 'flex', justifyContent: 'space-between', minWidth: '80vw', padding: spacing(2, 0) }}>
        <Typography variant="h4" color="secondary">
          Accounts
        </Typography>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="textSecondary">
            Contact Preferences:
          </Typography>
          <DoNotContactToggle
            style={{ marginLeft: 8 }}
            value={dncFilters}
            handleChange={newValues => {
              if (gridApi) {
                let doNotContactFilter = gridApi.getFilterInstance('do_not_contact_reasons')
                doNotContactFilter.setModel(newValues)
                gridApi.onFilterChanged()
              }
            }}
          />
        </div>
      </div>
    </FullscreenTable>
  )
}

enum DoNotContactToggleOption {
  OkToContact = 'OK to contact',
  ViaIntermediaryOwner = 'Via Intermediary/Owner',
  All = 'All',
}

interface DoNotContactToggleProps extends ToggleButtonGroupProps {
  value: string[] | null
  handleChange: (newValue: string[]) => void
}

const DoNotContactToggle: React.FC<DoNotContactToggleProps> = ({ value, handleChange, ...rest }) => {
  const onChange = (event: React.MouseEvent<HTMLElement>, option: DoNotContactToggleOption) => {
    switch (option) {
      case DoNotContactToggleOption.OkToContact:
        handleChange(['OK to contact'])
        break
      case DoNotContactToggleOption.ViaIntermediaryOwner:
        handleChange([
          doNotContactOptions[DoNotContactReason.HasIntermediary],
          doNotContactOptions[DoNotContactReason.IsHighSensitivity],
        ])
        break
      case DoNotContactToggleOption.All:
        handleChange(concat(['OK to contact'], values(doNotContactOptions)))
        break
    }
  }
  let newValue: DoNotContactToggleOption | null = null
  if (value === null) {
    newValue = DoNotContactToggleOption.All
  } else if (value.length === 1 && value.includes('OK to contact')) {
    newValue = DoNotContactToggleOption.OkToContact
  } else if (
    value.length === 2 &&
    value.includes(doNotContactOptions[DoNotContactReason.HasIntermediary]) &&
    value.includes(doNotContactOptions[DoNotContactReason.IsHighSensitivity])
  ) {
    newValue = DoNotContactToggleOption.ViaIntermediaryOwner
  } else {
    newValue = null
  }
  return (
    <ButtonGroup
      exclusive
      onChange={onChange}
      value={newValue}
      options={[
        { label: 'All', value: DoNotContactToggleOption.All },
        { label: 'Via Intermediary/Owner', value: DoNotContactToggleOption.ViaIntermediaryOwner },
        { label: ' OK to contact', value: DoNotContactToggleOption.OkToContact },
      ]}
      size={'small'}
      {...rest}
    />
  )
}
